import { ReactElement, useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
	EuiLoadingSpinner,
	EuiPage,
	EuiPageBody,
	EuiPageHeader,
	EuiSpacer,
	EuiText,
} from '@equipmentshare/ds2'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Select from '@mui/material/Select'
import { heapEvent } from '@equipmentshare/heaper'
import { NuminaSerialType, useUpdateItemMasterMutation } from '../../../../graphql'
import useItemMasterDetailQuery from '../detail/hooks/useItemMasterQuery'
import EquipmentMakeModelPicker from '../components/EquipmentMakeModelPicker'
import FormFooter, { FORM_FOOTER_HEIGHT, ValidationSummary } from '../components/FormFooter'
import { ASSET_MAKE_MODEL_ERROR } from '../utils'

export default function ItemMasterEdit(): ReactElement {
	const { id = '' } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const { itemMaster, loading, error } = useItemMasterDetailQuery( id )

	const [ flammable, setFlammable ] = useState( false )
	const [ fragile, setFragile ] = useState( false )
	const [ lithiumIonBattery, setLithiumIonBattery ] = useState( false )
	const [ isHazmat, setIsHazmat ] = useState( false )
	const [ isActive, setIsActive ] = useState( true )
	const [ numinaSerialType, setNuminaSerialType ] = useState<NuminaSerialType>( NuminaSerialType.Bulk )
	const [ equipmentMakeId, setEquipmentMakeId ] = useState<number | null>( null )
	const [ equipmentModelId, setEquipmentModelId ] = useState<number | null>( null )

	useEffect( () => {
		if ( itemMaster ) {
			setFlammable( itemMaster.flammable )
			setFragile( itemMaster.fragile )
			setLithiumIonBattery( itemMaster.lithiumIonBattery )
			setIsHazmat( itemMaster.isHazmat )
			setIsActive( itemMaster.isActive )
			setNuminaSerialType( itemMaster.numinaSerialType )
			setEquipmentMakeId( itemMaster.equipmentMakeId ? Number( itemMaster.equipmentMakeId ) : null )
			setEquipmentModelId( itemMaster.equipmentModelId ? Number( itemMaster.equipmentModelId ) : null )
		}
	}, [ itemMaster ] )

	const [ updateItemMaster, { loading: saving } ] = useUpdateItemMasterMutation()

	const isAsset = numinaSerialType === NuminaSerialType.Asset
	const assetIdsMissing = isAsset && ( !equipmentMakeId || !equipmentModelId )
	const [ submitted, setSubmitted ] = useState( false )

	const summary: ValidationSummary | null = ( submitted && assetIdsMissing )
		? { title: 'Fix the following to save:', errors: [ { field: 'equipmentMakeModel', message: ASSET_MAKE_MODEL_ERROR } ] }
		: null

	const handleTypeChange = ( next: NuminaSerialType ) => {
		setNuminaSerialType( next )
		if ( next !== NuminaSerialType.Asset ) {
			// Switching away from Asset invalidates the Make/Model pair.
			setEquipmentMakeId( null )
			setEquipmentModelId( null )
		}
	}

	const handleSubmit = async() => {
		setSubmitted( true )
		if ( assetIdsMissing ) {
			return
		}
		try {
			const result = await updateItemMaster( {
				variables: {
					input: {
						itemMasterId: id,
						flammable,
						fragile,
						lithiumIonBattery,
						isHazmat,
						isActive,
						numinaSerialType,
						equipmentMakeId: isAsset ? String( equipmentMakeId ) : null,
						equipmentModelId: isAsset ? String( equipmentModelId ) : null,
					},
				},
			} )

			if ( result.data?.updateItemMaster?.success ) {
				heapEvent( 'Inventory - Item Master - Edit - Submit - Success', { item_master_id: id } )
				navigate( `/manage/item-masters/${id}` )
			} else {
				heapEvent( 'Inventory - Item Master - Edit - Submit - Error', {} )
			}
		} catch {
			heapEvent( 'Inventory - Item Master - Edit - Submit - Error', {} )
		}
	}

	if ( loading ) {
		return (
			<EuiPage paddingSize="l">
				<EuiPageBody>
					<EuiLoadingSpinner size="xl" />
				</EuiPageBody>
			</EuiPage>
		)
	}

	if ( error || !itemMaster ) {
		return (
			<EuiPage paddingSize="l">
				<EuiPageBody>
					<EuiText><p>{error ? error.message : 'Item master not found.'}</p></EuiText>
				</EuiPageBody>
			</EuiPage>
		)
	}

	return (
		<EuiPage paddingSize="l">
			<EuiPageBody>
				<EuiPageHeader pageTitle="Edit Item Master" paddingSize="none" />
				<EuiSpacer />

				<Box maxWidth={480} sx={{ pb: `${FORM_FOOTER_HEIGHT}px` }}>
					<Stack spacing={2}>
						<Typography variant="subtitle2">Numina</Typography>
						<Box>
							<Typography variant="caption" color="text.secondary">Numina Serial Type</Typography>
							<Select
								value={numinaSerialType}
								onChange={( e ) => handleTypeChange( e.target.value as NuminaSerialType )}
								size="small"
								fullWidth
							>
								{Object.values( NuminaSerialType ).map( ( t ) => (
									<MenuItem key={t} value={t}>{t}</MenuItem>
								) )}
							</Select>
						</Box>
						{isAsset && (
							<EquipmentMakeModelPicker
								makeId={equipmentMakeId}
								modelId={equipmentModelId}
								onChange={( next ) => {
									setEquipmentMakeId( next.makeId )
									setEquipmentModelId( next.modelId )
								}}
								required
							/>
						)}
						<FormControlLabel
							control={
								<Checkbox
									checked={fragile}
									onChange={( e ) => setFragile( e.target.checked )}
								/>
							}
							label="Fragile"
						/>

						<Typography variant="subtitle2" mt={1}>Safety & Hazmat</Typography>
						<FormControlLabel
							control={
								<Checkbox
									checked={flammable}
									onChange={( e ) => setFlammable( e.target.checked )}
								/>
							}
							label="Flammable"
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={lithiumIonBattery}
									onChange={( e ) => setLithiumIonBattery( e.target.checked )}
								/>
							}
							label="Lithium Ion Battery"
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={isHazmat}
									onChange={( e ) => setIsHazmat( e.target.checked )}
								/>
							}
							label="Hazmat"
						/>
						<Typography variant="subtitle2" mt={1}>Status</Typography>
						<FormControlLabel
							control={
								<Checkbox
									checked={isActive}
									onChange={( e ) => setIsActive( e.target.checked )}
								/>
							}
							label="Active"
						/>
					</Stack>
				</Box>

				<FormFooter summary={summary}>
					<Button
						variant="outlined"
						onClick={() => navigate( `/manage/item-masters/${id}` )}
						disabled={saving}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleSubmit}
						disabled={saving || assetIdsMissing}
					>
						{saving ? 'Saving...' : 'Save Changes'}
					</Button>
				</FormFooter>
			</EuiPageBody>
		</EuiPage>
	)
}
